/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { RouterV2Enum } from '@/router/router-names';
import store from '@/store';
import { isHelloWechatMini } from '@/api/native/wechat';
import { ubtSendError } from '../ubt';
import {
  hybridQualityImages, qualityImages,
  taglessImages,
} from './config';

/** 首页UI主题 */
export enum HomeThemeEnums {
  /** 新版-老客升级 */
  AUTARKY_NEW = 'autarky_new',
  /** 送话费 */
  PHONE_BILL = 'phone_bill',
  /** 7天单车卡 */
  BIKE_CARD_7= 'bike_card_7',
  /** 顺风车券包 */
  CARPOOL_COUPON_PACKAGE = 'carpool_coupon_package',
  /** 借贷活动 */
  LOAN_ACTIVITY = 'loan_activity',
  /** 30天免息活动 */
  INTEREST_FREE_ACTIVITY = 'interest_free_activity',
  /** 30天免息活动 - 蓝色版本 */
  INTEREST_FREE_ACTIVITY_BLUE = 'interest_free_activity_blue',
}

export enum BannerClickActionEnums {
  JUMP_TO_CREDIT = 'jump_credit',
  JUMP_TO_APPLY_PRODUCT = 'jump_apply_product',
}

export interface HomeNavbarConfig {
  bgImageUrl: { webp: string; png: string},
  hybridBgImageUrl: { webp: string; png: string},
  priorityImageUrl?: string,
  bgTransitionStyle?: Dictionary<string>,
  isValidTheme?: boolean,
  themeLeftIconColor?: string,
}

export interface HomeBodyConfig {
  bgImageUrl: { webp: string; png: string},
  hybridBgImageUrl: { webp: string; png: string},
  priorityImageUrl?: string,
  bgTransitionStyle?: Dictionary<string>,
  clickAction?: string,
  isValidTheme?: boolean,
}

export interface HomeThemeConfig {
  /** 导航栏配置 */
  navbar: HomeNavbarConfig,
  /** 主内容配置 */
  body: HomeBodyConfig,
  /** 页面名称(匹配对应) */
  pageName: RouterV2Enum[],
}

export const HomeThemeConfigMap: Record<HomeThemeEnums, HomeThemeConfig | null> = {
  [HomeThemeEnums.AUTARKY_NEW]: {
    navbar: {
      bgImageUrl: qualityImages.autarkyNavbarBgImage,
      hybridBgImageUrl: hybridQualityImages.autarkyNavbarBgImage,
      bgTransitionStyle: {
        backgroundColor: '#f4993a',
      },
    },
    body: {
      bgImageUrl: isHelloWechatMini()
        ? qualityImages.miniMpAutarkyContentBgImage : qualityImages.autarkyContentBgImage,
      hybridBgImageUrl: hybridQualityImages.autarkyContentBgImage,
      bgTransitionStyle: {
        backgroundImage: 'linear-gradient(180deg, #ff7d25 52.68%, #f0f3f5 100%)',
      },
    },
    pageName: [RouterV2Enum.qualityHomePage, RouterV2Enum.qualityAliminiHome],
  },
  [HomeThemeEnums.PHONE_BILL]: {
    navbar: {
      bgImageUrl: taglessImages.phoneBillNavbarBgImage,
      hybridBgImageUrl: taglessImages.phoneBillNavbarBgImage,
      bgTransitionStyle: {
        backgroundImage: 'linear-gradient( #fe5927, #fe5927)',
      },
    },
    body: {
      bgImageUrl: taglessImages.phoneBillContentBgImage,
      hybridBgImageUrl: taglessImages.phoneBillContentBgImage,
      bgTransitionStyle: {
        backgroundImage: 'linear-gradient(180deg, #fc5f2a 0%, rgb(252 150 71 / 2%) 100%)',
      },
      clickAction: BannerClickActionEnums.JUMP_TO_CREDIT,
    },
    pageName: [RouterV2Enum.taglessHome, RouterV2Enum.home],
  },
  [HomeThemeEnums.BIKE_CARD_7]: {
    navbar: {
      bgImageUrl: taglessImages.bikeCard7NavbarBgImage,
      hybridBgImageUrl: taglessImages.bikeCard7NavbarBgImage,
      bgTransitionStyle: {
        backgroundImage: 'linear-gradient( #fe5927, #fe5927)',
      },
    },
    body: {
      bgImageUrl: taglessImages.bikeCard7ContentBgImage,
      hybridBgImageUrl: taglessImages.bikeCard7ContentBgImage,
      bgTransitionStyle: {
        backgroundImage: 'linear-gradient(180deg, #fc5f2a 0%, rgb(252 150 71 / 2%) 100%)',
      },
      clickAction: BannerClickActionEnums.JUMP_TO_CREDIT,
    },
    pageName: [RouterV2Enum.taglessHome, RouterV2Enum.home],
  },
  [HomeThemeEnums.CARPOOL_COUPON_PACKAGE]: {
    navbar: {
      bgImageUrl: taglessImages.carpoolCouponPackageNavbarBgImage,
      hybridBgImageUrl: taglessImages.carpoolCouponPackageNavbarBgImage,
      bgTransitionStyle: {
        backgroundImage: 'linear-gradient( #fe5927, #fe5927)',
      },
    },
    body: {
      bgImageUrl: taglessImages.carpoolCouponPackageContentBgImage,
      hybridBgImageUrl: taglessImages.carpoolCouponPackageContentBgImage,
      bgTransitionStyle: {
        backgroundImage: 'linear-gradient(180deg, #fc5f2a 0%, rgb(252 150 71 / 2%) 100%)',
      },
      clickAction: BannerClickActionEnums.JUMP_TO_CREDIT,
    },
    pageName: [RouterV2Enum.taglessHome, RouterV2Enum.home],
  },
  [HomeThemeEnums.LOAN_ACTIVITY]: {
    navbar: {
      bgImageUrl: taglessImages.loanActivityNavbarBgImage,
      hybridBgImageUrl: taglessImages.loanActivityNavbarBgImage,
      bgTransitionStyle: {
        backgroundImage: 'linear-gradient( #fe5927, #fe5927)',
      },
    },
    body: {
      bgImageUrl: taglessImages.loanActivityContentBgImage,
      hybridBgImageUrl: taglessImages.loanActivityContentBgImage,
      bgTransitionStyle: {
        backgroundImage: 'linear-gradient(180deg, #fc5f2a 0%, rgb(252 150 71 / 2%) 100%)',
      },
      clickAction: BannerClickActionEnums.JUMP_TO_CREDIT,
    },
    pageName: [RouterV2Enum.taglessHome, RouterV2Enum.home],
  },
  [HomeThemeEnums.INTEREST_FREE_ACTIVITY]: null, // 30天免息活动重定向到v3/home，不使用此配置
  [HomeThemeEnums.INTEREST_FREE_ACTIVITY_BLUE]: null, // 30天免息活动蓝色版本重定向到v3/home，不使用此配置
};

/**
 * 获取页面主题body配置
 * @param themeType 主题类型
 * @param pageName 限定页面名称
 * @returns
 */
export function getHomeThemeBodyConfig(
  themeType: HomeThemeEnums | null,
  pageName?: string,
): HomeBodyConfig {
  try {
    const config = (themeType && HomeThemeConfigMap[themeType]) || {} as HomeThemeConfig;
    const isSupportWebp = store.state.base && store.state.base.isSupportWebp;
    const isHybridMode = store.state.base && store.state.base.isHybridAppMode;

    if (config.body) {
      const bgImageUrl = isHybridMode ? config.body.hybridBgImageUrl : config.body.bgImageUrl;

      return {
        ...config.body,
        priorityImageUrl: isSupportWebp ? bgImageUrl.webp : bgImageUrl.png,
        isValidTheme: pageName ? config.pageName.includes(pageName as RouterV2Enum) : false,
      };
    }
  } catch (err) {
    ubtSendError(err as Error);
  }

  return { isValidTheme: false } as HomeBodyConfig;
}
/**
 * 获取页面主题对应导航栏配置
 * @param themeType 主题类型
 * @param pageName 限定页面名称
 * @returns
 */
export function getThemeNavbarConfig(
  themeType: HomeThemeEnums | null,
  pageName?: string,
): HomeNavbarConfig {
  try {
    const config = (themeType && HomeThemeConfigMap[themeType]) || {} as HomeThemeConfig;
    const isSupportWebp = store.state.base && store.state.base.isSupportWebp;
    const isHybridMode = store.state.base && store.state.base.isHybridAppMode;

    if (config.navbar) {
      const bgImageUrl = isHybridMode ? config.navbar.hybridBgImageUrl : config.navbar.bgImageUrl;

      return {
        ...config.navbar,
        priorityImageUrl: isSupportWebp
          ? bgImageUrl.webp : bgImageUrl.png,
        isValidTheme: pageName ? config.pageName.includes(pageName as RouterV2Enum) : false,
      };
    }
  } catch (err) {
    ubtSendError(err as Error);
  }

  return { isValidTheme: false } as HomeNavbarConfig;
}
